import React, { useEffect, useRef, useState } from 'react';
import { ExcelRenderer } from 'react-excel-renderer';
import {
  MdCheckCircle,
  MdClose,
  MdDescription,
  MdDownload,
  MdErrorOutline,
  MdUploadFile,
} from 'react-icons/md';
import { useUser } from '../../../../contexts/userContext';
import { useColumns } from '../../../../contexts/columnsContext';
import { useCard } from '../../../../contexts/cardContext';
import api from '../../../../services/api';
import './NewImportExcelModal.css';

const SAMPLE_FILE_URL = 'https://firebasestorage.googleapis.com/v0/b/suiteflow-1c751.appspot.com/o/Planilha%20de%20importa%C3%A7%C3%A3o%20SuiteFlow.xlsx?alt=media';

const EXPECTED_COLUMNS = [
  'Número do orçamento',
  'Versão',
  'Nome do cliente',
  'Data',
  'Valor',
  'Nome da entidade',
];

function readExcelFile(file) {
  return new Promise((resolve, reject) => {
    ExcelRenderer(file, (error, response) => {
      if (error) {
        reject(error);
        return;
      }

      resolve(response?.rows || []);
    });
  });
}

function NewImportExcelModal({ onClose, onImported }) {
  const { user, listAllUsers } = useUser();
  const { columns } = useColumns();
  const { addHistoricoCardContext } = useCard();
  const fileInputRef = useRef(null);
  const closeButtonRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [result, setResult] = useState(null);

  useEffect(() => {
    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !loading) onClose();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [loading, onClose]);

  const selectFile = (file) => {
    if (!file) return;

    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!['xls', 'xlsx'].includes(extension)) {
      setSelectedFile(null);
      setResult(null);
      setMessage({ tone: 'danger', text: 'Escolha uma planilha nos formatos .xlsx ou .xls.' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setSelectedFile(file);
    setResult(null);
    setProgress({ current: 0, total: 0 });
    setMessage(null);
  };

  const handleFileChange = (event) => selectFile(event.target.files?.[0]);

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    if (!loading) selectFile(event.dataTransfer.files?.[0]);
  };

  const handleClose = () => {
    if (!loading) onClose();
  };

  const handleImport = async () => {
    if (!selectedFile || loading) {
      setMessage({ tone: 'danger', text: 'Selecione a planilha que deseja importar.' });
      return;
    }

    const importColumn = (columns || []).find((column) => column.name === 'Import');
    if (!importColumn?.id) {
      setMessage({ tone: 'danger', text: 'A etapa “Import” não está ativa neste pipeline.' });
      return;
    }

    setLoading(true);
    setResult(null);
    setMessage({ tone: 'info', text: 'Lendo e validando a planilha…' });

    try {
      const rows = await readExcelFile(selectedFile);
      const dataRows = rows
        .slice(1)
        .filter((row) => Array.isArray(row) && row.some((cell) => cell !== null && cell !== undefined && String(cell).trim() !== ''));

      if (!dataRows.length) {
        setMessage({ tone: 'danger', text: 'A planilha não possui linhas para importar.' });
        return;
      }

      let imported = 0;
      let skipped = 0;
      let failed = 0;

      setProgress({ current: 0, total: dataRows.length });

      // O envio sequencial evita uma rajada de gravações e preserva a mesma regra do importador clássico.
      for (let index = 0; index < dataRows.length; index += 1) {
        const [documentNumber, version, name, , value, entityReference] = dataRows[index];

        const entity = (listAllUsers || []).find((item) => item.entidade === entityReference);
        const validVersion = version === 'Versão 1' || Number(version) === 1;
        const validEntityReference = entityReference !== null && entityReference !== undefined && String(entityReference).trim() !== '';

        if (!validVersion || !validEntityReference || !entity?.id) {
          skipped += 1;
          setProgress({ current: index + 1, total: dataRows.length });
          continue;
        }

        try {
          const response = await api.post('/card/import', {
            name: name || '',
            state: '',
            city: '',
            fone: '',
            email: '',
            column_id: importColumn.id,
            entity_id: entity.id,
            empresa_id: user.empresa_id,
            document_number: documentNumber || '',
            cost_value: value || 0,
          });

          const cardId = response.data?.card_id;
          if (cardId) {
            await addHistoricoCardContext('Card importado via Excel', cardId, user.id);
          }
          imported += 1;
        } catch (error) {
          console.error('Erro ao importar linha da planilha:', error);
          failed += 1;
        }

        setProgress({ current: index + 1, total: dataRows.length });
      }

      const nextResult = { imported, skipped, failed, total: dataRows.length };
      setResult(nextResult);

      if (imported > 0) {
        setMessage({
          tone: failed > 0 || skipped > 0 ? 'warning' : 'success',
          text: failed > 0
            ? 'Importação concluída com algumas linhas não processadas.'
            : skipped > 0
              ? 'Importação concluída. Algumas linhas inválidas foram ignoradas.'
              : 'Importação concluída com sucesso.',
        });
        if (onImported) {
          try {
            await onImported(nextResult);
          } catch (refreshError) {
            console.error('Erro ao atualizar o quadro após a importação:', refreshError);
          }
        }
      } else {
        setMessage({
          tone: 'danger',
          text: failed > 0
            ? 'Nenhuma linha pôde ser importada. Tente novamente.'
            : 'Nenhuma linha válida foi encontrada. Confira a versão e a entidade informadas.',
        });
      }
    } catch (error) {
      console.error('Erro ao ler a planilha:', error);
      setMessage({ tone: 'danger', text: 'Não foi possível ler essa planilha. Confira o arquivo e tente novamente.' });
    } finally {
      setLoading(false);
    }
  };

  const progressPercentage = progress.total
    ? Math.round((progress.current / progress.total) * 100)
    : 0;

  return (
    <div className="new-home-import-backdrop" onMouseDown={(event) => event.target === event.currentTarget && handleClose()}>
      <section className="new-home-import-modal" role="dialog" aria-modal="true" aria-labelledby="new-home-import-title">
        <header className="new-home-import-header">
          <div className="new-home-import-heading-icon" aria-hidden="true"><MdUploadFile /></div>
          <div>
            <span>Importação de dados</span>
            <h2 id="new-home-import-title">Importar planilha</h2>
            <p>Adicione novos cards ao pipeline a partir do modelo padrão.</p>
          </div>
          <button ref={closeButtonRef} type="button" className="new-home-import-close" onClick={handleClose} disabled={loading} aria-label="Fechar importação">
            <MdClose />
          </button>
        </header>

        <div className="new-home-import-body">
          <div className="new-home-import-section-heading">
            <div><span>1</span><div><strong>Selecione o arquivo</strong><small>Formatos aceitos: XLSX ou XLS</small></div></div>
          </div>

          <input
            ref={fileInputRef}
            id="new-home-import-file"
            className="new-home-import-file-input"
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            onChange={handleFileChange}
            disabled={loading}
          />
          <label
            htmlFor="new-home-import-file"
            className={`new-home-import-dropzone${isDragging ? ' is-dragging' : ''}${selectedFile ? ' has-file' : ''}${loading ? ' is-disabled' : ''}`}
            onDragEnter={(event) => { event.preventDefault(); if (!loading) setIsDragging(true); }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setIsDragging(false); }}
            onDrop={handleDrop}
          >
            <span className="new-home-import-file-icon" aria-hidden="true">{selectedFile ? <MdDescription /> : <MdUploadFile />}</span>
            <span className="new-home-import-file-copy">
              <strong>{selectedFile ? selectedFile.name : 'Escolha ou arraste a planilha aqui'}</strong>
              <small>{selectedFile ? `${(selectedFile.size / 1024).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} KB · Clique para trocar o arquivo` : 'O arquivo será validado antes do envio'}</small>
            </span>
            <span className="new-home-import-browse">{selectedFile ? 'Trocar' : 'Procurar'}</span>
          </label>

          <div className="new-home-import-section-heading">
            <div><span>2</span><div><strong>Confira a estrutura</strong><small>A primeira linha deve conter estes campos, nesta ordem</small></div></div>
            <button type="button" onClick={() => window.open(SAMPLE_FILE_URL, '_blank', 'noopener,noreferrer')}>
              <MdDownload /> Baixar modelo
            </button>
          </div>

          <div className="new-home-import-columns" aria-label="Ordem das colunas da planilha">
            {EXPECTED_COLUMNS.map((column, index) => (
              <div key={column}><span>{index + 1}</span><strong>{column}</strong></div>
            ))}
          </div>

          {message && (
            <div className={`new-home-import-message tone-${message.tone}`} role="status" aria-live="polite">
              {message.tone === 'success' ? <MdCheckCircle /> : ['danger', 'warning'].includes(message.tone) ? <MdErrorOutline /> : <MdDescription />}
              <span>{message.text}</span>
            </div>
          )}

          {loading && progress.total > 0 && (
            <div className="new-home-import-progress" aria-label={`Importando ${progress.current} de ${progress.total} linhas`}>
              <div><strong>Importando cards</strong><span>{progress.current} de {progress.total}</span></div>
              <div className="new-home-import-progress-track"><span style={{ width: `${progressPercentage}%` }} /></div>
            </div>
          )}

          {result && (
            <div className="new-home-import-result">
              <div><strong>{result.imported}</strong><span>Importados</span></div>
              <div><strong>{result.skipped}</strong><span>Ignorados</span></div>
              <div><strong>{result.failed}</strong><span>Com erro</span></div>
            </div>
          )}
        </div>

        <footer className="new-home-import-footer">
          <p>Os cards válidos serão adicionados à etapa <strong>Import</strong>.</p>
          <div>
            <button type="button" className="new-home-import-secondary" onClick={handleClose} disabled={loading}>{result?.imported ? 'Concluir' : 'Cancelar'}</button>
            {!result?.imported && (
              <button type="button" className="new-home-import-primary" onClick={handleImport} disabled={loading || !selectedFile}>
                <MdUploadFile /> {loading ? 'Importando…' : 'Importar cards'}
              </button>
            )}
          </div>
        </footer>
      </section>
    </div>
  );
}

export default NewImportExcelModal;
